import { HKPImage } from "../../media/HKPImage";
import React, { useEffect, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { Button, Input, Spinner } from "reactstrap";
import { getPublicPiece, unlockPublicPiece } from "../../services/collectorApi";
import { useCollectorPwa } from "../../helpers/useCollectorPwa";
import McBackLink from "./McBackLink";
import "./my-collection.scss";

const META_KEYS = [
  "itemCategory",
  "craftType",
  "color",
  "fabric",
  "clusterType",
  "occasion",
  "makerName",
] as const;

type MetaKey = (typeof META_KEYS)[number];

const fieldLabel: Record<MetaKey, string> = {
  itemCategory: "Category",
  craftType: "Craft",
  color: "Color",
  fabric: "Fabric",
  clusterType: "Region",
  occasion: "Occasion",
  makerName: "Company / brand / artisan",
};

type PublicPiece = Partial<Record<MetaKey, string>> & {
  visibility?: string;
  requiresPasskey?: boolean;
  unlocked?: boolean;
  title?: string;
  story?: string;
  coverPhotoUrl?: string;
  additionalImageUrls?: string[];
  hkpCertified?: boolean;
  studioName?: string;
};

const pretty = (value?: string) => {
  if (!value) return "";
  const spaced = value.replace(/_/g, " ").trim();
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
};

const PublicPiecePage: React.FC = () => {
  useCollectorPwa();
  const { collectorItemId: paramId } = useParams<{ collectorItemId: string }>();
  const location = useLocation();
  const collectorItemId = paramId || location.pathname.split("/").filter(Boolean)[2];
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<PublicPiece | null>(null);
  const [error, setError] = useState("");
  const [passkey, setPasskey] = useState("");
  const [unlocking, setUnlocking] = useState(false);
  const [unlockError, setUnlockError] = useState("");

  useEffect(() => {
    if (!collectorItemId) {
      setError("This piece is no longer available");
      setLoading(false);
      return;
    }
    setLoading(true);
    getPublicPiece(collectorItemId)
      .then((res) => setData(res.data?.data || res.data))
      .catch(() => setError("This piece is no longer available"))
      .finally(() => setLoading(false));
  }, [collectorItemId]);

  const submitPasskey = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!collectorItemId || !passkey.trim()) return;
    setUnlocking(true);
    setUnlockError("");
    try {
      const res = await unlockPublicPiece(collectorItemId, passkey.trim());
      setData({ ...(res.data?.data || res.data), unlocked: true });
    } catch (err: any) {
      const status = err?.response?.status;
      setUnlockError(
        status === 429
          ? "Too many attempts. Please try again later."
          : err?.response?.data?.message || "Incorrect passkey"
      );
    } finally {
      setUnlocking(false);
    }
  };

  const miniHeader = (
    <header className="mc-public-mini-header">
      <div className="d-flex justify-content-between align-items-center">
        <Link to="/home" className="mc-wordmark">
          <span className="sp1">Hastkala</span> <span className="sp2">Paramanak</span>
        </Link>
        <McBackLink to="/home">Back to website</McBackLink>
      </div>
    </header>
  );

  if (loading) {
    return (
      <div className="mc-public-piece text-center py-5">
        {miniHeader}
        <Spinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="mc-public-piece">
        {miniHeader}
        <div className="mc-public-shell text-center text-muted">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (data?.visibility === "PRIVATE" && data?.requiresPasskey && !data?.unlocked) {
    return (
      <div className="mc-public-piece">
        {miniHeader}
        <div className="mc-public-shell" style={{ maxWidth: 460 }}>
          <p className="mc-public-kicker">Private piece</p>
          <h1 className="mc-public-title">{data.title || "Private piece"}</h1>
          <p className="text-muted">
            The owner has kept this journal private. Enter their passkey to view it.
          </p>
          <form onSubmit={submitPasskey}>
            <label className="labelName" htmlFor="publicPiecePasskey">Passkey</label>
            <Input
              id="publicPiecePasskey"
              type="password"
              value={passkey}
              onChange={(e) => setPasskey(e.target.value)}
              placeholder="Passkey"
              autoComplete="off"
              className="inputField mb-2"
            />
            {unlockError && <p className="mc-field-error" role="alert">{unlockError}</p>}
            <Button className="mc-btn-primary mc-btn-block" type="submit" disabled={unlocking || !passkey.trim()}>
              {unlocking ? "Checking…" : "Unlock"}
            </Button>
          </form>
          <p className="small text-muted mt-4 mb-0">
            Owner? <a href="/my-collection/login">Log in to reset your passkey</a>
          </p>
        </div>
      </div>
    );
  }

  const maker = data?.studioName?.trim() || data?.makerName?.trim();
  const metaFields = META_KEYS.filter((key) => Boolean(data?.[key]) && !(maker && key === "makerName"));
  const story = data?.story?.trim();
  const title = data?.title?.trim() || "Collection piece";
  const extraPhotos = data?.additionalImageUrls ?? [];

  return (
    <article className="mc-public-piece">
      {miniHeader}
      <div className="mc-public-shell">
        <div className="mc-public-layout">
          <div className="mc-public-photo">
            {data?.coverPhotoUrl ? (
              <HKPImage allowMaster={false} preset="PRODUCT_DETAIL" legacyUrl={data.coverPhotoUrl} alt={title} />
            ) : (
              <div className="mc-public-photo-placeholder" aria-hidden="true" />
            )}
          </div>
          <div className="mc-public-copy">
            <p className="mc-public-kicker">
              {data?.unlocked ? "Private journal · unlocked" : "My Collection"}
            </p>
            <h1 className="mc-public-title">{title}</h1>
            {data?.hkpCertified && (
              <p className="mc-public-cert-badge">HastKalaPramanak certified</p>
            )}
            {maker && <p className="mc-public-maker">{pretty(maker)}</p>}
            {story ? (
              <p className="mc-public-story">{story}</p>
            ) : (
              <p className="mc-public-empty">The owner hasn’t added a note yet.</p>
            )}
            {metaFields.length > 0 && (
              <section className="mc-public-meta">
                <h2 className="h6 text-uppercase text-muted mb-3">Details</h2>
                <dl className="row mb-0">
                  {metaFields.map((key) => (
                    <React.Fragment key={key}>
                      <dt className="col-5 col-sm-4">{fieldLabel[key]}</dt>
                      <dd className="col-7 col-sm-8">{pretty(data?.[key])}</dd>
                    </React.Fragment>
                  ))}
                </dl>
              </section>
            )}
          </div>
        </div>
        {extraPhotos.length > 0 && (
          <section className="mc-public-gallery mt-4">
            <h2 className="h6 text-uppercase text-muted mb-3">More photos</h2>
            <div className="mc-thumb-grid">
              {extraPhotos.map((url, index) => (
                <div key={`${url}-${index}`} className="mc-thumb">
                  <HKPImage allowMaster={false} preset="PRODUCT_DETAIL" legacyUrl={url} alt="" />
                </div>
              ))}
            </div>
          </section>
        )}
        <footer className="mt-4 pt-3 border-top text-muted small">
          Journal powered by HastKalaPramanak · My Collection
        </footer>
      </div>
    </article>
  );
};

export default PublicPiecePage;

